import axios from 'axios';
import { getBaseUrl } from 'utils/url';

export interface Deal {
  dealId: string;
  companyId: string;
  companyName: string;
  amount: number;
  stage: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  closedWonAt?: {
    _seconds: number;
    _nanoseconds: number;
  } | null;
}

export interface DealStage {
  deals: Deal[];
  total: number;
}

export interface JobRun {
  accountId: string;
  triggerType: string;
  status: string;
  processed: number;
  successful: number;
  failed: number;
  errors: any[];
  totalDealsStored: number;
  completedAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  startedAt: {
    _seconds: number;
    _nanoseconds: number;
  };
}

export interface JobStatus {
  totalRuns: number;
  successfulRuns: number;
  failedRuns: number;
  lastRun: JobRun;
  averageDealsStored: number;
  successRate: number;
}

export interface AnalyticsData {
  closedwon: DealStage;
  opportunity: DealStage;
  proposal: DealStage;
  other: DealStage;
  activePartners: number;
  jobStatus: JobStatus;
}

export interface AnalyticsResponse {
  data: AnalyticsData;
  success: boolean;
}

export interface AnalyticsSummary {
  totalRecords: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  totalAmount: number;
  totalDeals: number;
  totalCompanies: number;
  dealsByDate: {
    [dateKey: string]: {
      amount: number;
      dealCount: number;
      deals: Deal[];
    };
  };
}

export interface AnalyticsRangeResponse {
  data: AnalyticsData[];
  summary: AnalyticsSummary;
  success: boolean;
}

export interface TriggerResponse {
  success: boolean;
  message: string;
  data?: any;
}

class AnalyticsService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getBaseUrl();
  }

  private async makeRequest<T>(
    endpoint: string,
    token: string,
    method: 'GET' | 'POST' = 'GET',
    params?: Record<string, string>,
  ): Promise<T> {
    const url = new URL(`${this.baseUrl}${endpoint}`);

    if (params) {
      Object.keys(params).forEach((key) => {
        url.searchParams.append(key, params[key]);
      });
    }

    const response = await axios({
      method,
      url: url.toString(),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  }

  async getAnalyticsByDate(
    date: string,
    token: string,
  ): Promise<AnalyticsData> {
    return this.makeRequest<AnalyticsData>(
      `/api/analytics/date/${date}`,
      token,
    );
  }

  async getAnalyticsByDateRange(
    startDate: string,
    endDate: string,
    token: string,
  ): Promise<AnalyticsRangeResponse> {
    return this.makeRequest<AnalyticsRangeResponse>(
      '/api/analytics/range',
      token,
      'GET',
      { startDate, endDate },
    );
  }

  async getLatestAnalytics(token: string): Promise<AnalyticsData> {
    return this.makeRequest<AnalyticsData>('/api/analytics/latest', token);
  }

  async getAvailableDates(
    token: string,
  ): Promise<{ data: string[]; success: boolean }> {
    return this.makeRequest<{ data: string[]; success: boolean }>(
      '/api/analytics/dates',
      token,
    );
  }

  async getCurrentMonthAnalytics(token: string): Promise<AnalyticsData> {
    return this.makeRequest<AnalyticsData>(
      '/api/analytics/current-month',
      token,
    );
  }

  async getAllTimeAnalytics(token: string): Promise<AnalyticsData> {
    return this.makeRequest<AnalyticsData>('/api/analytics/all-time', token);
  }

  async triggerAnalytics(token: string): Promise<TriggerResponse> {
    return this.makeRequest<TriggerResponse>(
      '/api/analytics/trigger',
      token,
      'POST',
    );
  }
}

export const analyticsService = new AnalyticsService();
